import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import PropTypes from 'prop-types';
import { FiDownload, FiMail } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { MdArrowBack } from 'react-icons/md';
import { toast } from '../../toasts';
import BusyOverlay from '../BusyOverlay/BusyOverlay';
import { Button, FormField, controlClass } from '../EntityList';
import PaymentService from '../../services/payment.service';
import { getError } from '../../utils';

const normalizePhone = (raw) => raw?.replace(/\D/g, '') || '';
const isValidPhone = (raw) => {
	const digits = normalizePhone(raw);
	return digits.length === 11 && digits.startsWith('598');
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

/**
 * Both callers (PaymentEditor and ContractPaymentEditor, post-save) render this
 * straight into a page whose container has no resolved height, so the viewer
 * gets an explicit viewport-derived height. It used to ask for `height: 100%`
 * plus a `flex: 1` iframe, which collapses without a sized parent — that is
 * what clipped the receipt.
 */
function PaymentReceiptStep({ paymentId, onDone }) {
	const [loading, setLoading] = useState(true);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [emailInput, setEmailInput] = useState('');
	const [emailSending, setEmailSending] = useState(false);
	const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
	const [phoneInput, setPhoneInput] = useState('');
	const [sharing, setSharing] = useState(false);
	const defaultPhoneRef = useRef(null);

	useEffect(() => {
		if (!paymentId) return;
		let objectUrl = null;
		const load = async () => {
			setLoading(true);
			try {
				const [blob, phone] = await Promise.all([
					PaymentService.getReceiptBlob(paymentId),
					PaymentService.getClientPhone(paymentId).catch(() => null),
				]);
				objectUrl = URL.createObjectURL(blob);
				setPdfUrl(objectUrl);
				defaultPhoneRef.current = phone || '';
			} catch (err) {
				toast.error(getError(err));
			} finally {
				setLoading(false);
			}
		};
		load();
		return () => {
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [paymentId]);

	const handleDownload = () => {
		PaymentService.downloadReceipt(paymentId);
	};

	const handleWhatsApp = () => {
		setPhoneInput(defaultPhoneRef.current || '');
		setShowWhatsAppModal(true);
	};

	const handleSendWhatsApp = async () => {
		if (!isValidPhone(phoneInput)) {
			toast.error('Ingrese un número de WhatsApp válido con código de país (ej: 59891234567)');
			return;
		}
		try {
			setSharing(true);
			const res = await PaymentService.createReceiptShareLink(paymentId);
			const { url } = res.data;
			const digits = normalizePhone(phoneInput);
			const text = encodeURIComponent(`Hola, te comparto el recibo de cobro para descargar: ${url}`);
			window.open(`https://wa.me/${digits}?text=${text}`, '_blank', 'noopener,noreferrer');
			toast.success('Enlace generado');
			setShowWhatsAppModal(false);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setSharing(false);
		}
	};

	const handleEmail = () => {
		setEmailInput('');
		setShowEmailModal(true);
	};

	const handleSendEmail = async () => {
		if (!emailInput) {
			toast.error('Ingrese un email');
			return;
		}
		setEmailSending(true);
		try {
			await PaymentService.sendReceiptByEmail(paymentId, emailInput);
			toast.success('Email enviado correctamente');
			setShowEmailModal(false);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setEmailSending(false);
		}
	};

	return (
		<>
			<Modal show={showWhatsAppModal} onHide={() => setShowWhatsAppModal(false)} animation={false} centered>
				<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton>
					<Modal.Title className='tw:text-[15px] tw:font-semibold'>Enviar recibo por WhatsApp</Modal.Title>
				</Modal.Header>
				<Modal.Body className='tw:bg-surface tw:px-5 tw:py-5'>
					<FormField id='receipt-whatsapp-phone' label='Número de WhatsApp'>
						<Form.Control
							id='receipt-whatsapp-phone'
							type='tel'
							className={controlClass}
							placeholder='59891234567'
							value={phoneInput}
							onChange={(e) => setPhoneInput(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSendWhatsApp()}
							disabled={sharing}
						/>
						<p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-ink-3'>
							Incluir código de país. Ej: 59891234567 (Uruguay: 598 + 8 dígitos)
						</p>
						<FieldError
							message={phoneInput && !isValidPhone(phoneInput) ? 'Número inválido. Ej: 59891234567' : null}
						/>
					</FormField>
				</Modal.Body>
				<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
					<Button onClick={() => setShowWhatsAppModal(false)} disabled={sharing}>
						Cancelar
					</Button>
					<Button
						variant='primary'
						onClick={handleSendWhatsApp}
						disabled={sharing || !phoneInput || !isValidPhone(phoneInput)}
					>
						{sharing ? 'Generando enlace...' : 'Enviar'}
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal show={showEmailModal} onHide={() => setShowEmailModal(false)} animation={false} centered>
				<Modal.Header className='tw:border-b tw:border-border tw:bg-surface-2' closeButton>
					<Modal.Title className='tw:text-[15px] tw:font-semibold'>Enviar recibo por email</Modal.Title>
				</Modal.Header>
				<Modal.Body className='tw:bg-surface tw:px-5 tw:py-5'>
					<FormField id='receipt-email' label='Email'>
						<Form.Control
							id='receipt-email'
							type='email'
							className={controlClass}
							placeholder='ejemplo@correo.com'
							value={emailInput}
							onChange={(e) => setEmailInput(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleSendEmail()}
							disabled={emailSending}
						/>
					</FormField>
				</Modal.Body>
				<Modal.Footer className='tw:flex tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2'>
					<Button onClick={() => setShowEmailModal(false)} disabled={emailSending}>
						Cancelar
					</Button>
					<Button variant='primary' onClick={handleSendEmail} disabled={emailSending || !emailInput}>
						{emailSending ? 'Enviando...' : 'Enviar'}
					</Button>
				</Modal.Footer>
			</Modal>

			<div className='tw:flex tw:flex-col tw:gap-4'>
				<div className='tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface tw:px-5 tw:py-3.5 tw:shadow-sm'>
					<div className='tw:text-[12.5px] tw:text-ink-2'>Recibo de cobro generado.</div>
					<div className='tw:flex tw:flex-wrap tw:gap-2'>
						<Button icon={<FiDownload />} onClick={handleDownload} disabled={loading || !pdfUrl}>
							Descargar
						</Button>
						<Button icon={<FaWhatsapp />} onClick={handleWhatsApp} disabled={loading}>
							Enviar por WhatsApp
						</Button>
						<Button icon={<FiMail />} onClick={handleEmail} disabled={loading}>
							Enviar por Email
						</Button>
						{onDone && (
							<Button variant='primary' icon={<MdArrowBack />} onClick={onDone}>
								Volver
							</Button>
						)}
					</div>
				</div>

				<BusyOverlay active={loading} />

				{!loading && pdfUrl && (
					<div className='tw:h-[calc(100vh-320px)] tw:min-h-[420px] tw:overflow-hidden tw:rounded-panel tw:border tw:border-border tw:bg-surface-2 tw:shadow-sm'>
						<iframe
							src={pdfUrl}
							title='Recibo de cobro'
							className='tw:h-full tw:w-full'
							style={{ border: 0, display: 'block' }}
						/>
					</div>
				)}
			</div>
		</>
	);
}

PaymentReceiptStep.propTypes = {
	paymentId: PropTypes.string,
	onDone: PropTypes.func,
};

export default PaymentReceiptStep;
